import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { FeatherArrowLeft, FeatherDownload, FeatherPrinter, FeatherFileText } from '../../components/ui/icons';
import DateSelector from '../../components/DailyReview/DateSelector';
import TodayOutcomeComponent from '../../components/DailyReview/TodayOutcome';
import NotesPreview from '../../components/DailyReview/NotesPreview';
import dailyReviewService from '../../services/dailyReviewService';
import { getTodayLocalDate } from '../../utils/date';
import './TodayOutcome.css';

const TodayOutcome = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [date, setDate] = useState(() => {
        const dateParam = searchParams.get('date');
        return dateParam || getTodayLocalDate();
    });
    const [outcomeData, setOutcomeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [notesData, setNotesData] = useState(null);
    const [isSavingGoal, setIsSavingGoal] = useState(false);

    useEffect(() => {
        fetchOutcomeData();
    }, [projectId, date]);

    const fetchOutcomeData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dailyReviewService.getTodayProjectOutcome(projectId, date);
            setOutcomeData(data);
        } catch (err) {
            console.error('Error fetching today\'s project outcome:', err);
            setError('Failed to load today\'s project outcome. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (newDate) => {
        setDate(newDate);
        // Update URL to reflect the new date
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('date', newDate);
        navigate(`/daily-work-plan-review/project/${projectId}/today-outcome?${newSearchParams.toString()}`, { replace: true });
    };

    const handleBack = () => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('date', date);
        navigate(`/daily-work-plan-review/project/${projectId}?${newSearchParams.toString()}`);
    };

    const handleExport = () => {
        // TODO: Implement export functionality
        window.print();
    };

    const handlePrint = () => {
        window.print();
    };

    const handleViewNotes = async () => {
        try {
            const summary = await dailyReviewService.getProjectSummary(projectId, date);
            setNotesData(summary.reviewNotes || {});
            setShowNotesModal(true);
        } catch (err) {
            console.error('Error fetching notes:', err);
            alert('Failed to load notes. Please try again.');
        }
    };

    const handlePersonalGoalToggle = async (goalId, currentStatus) => {
        if (isSavingGoal) return; // Prevent multiple simultaneous saves

        try {
            setIsSavingGoal(true);
            
            // Fetch current notes
            const summary = await dailyReviewService.getProjectSummary(projectId, date);
            const currentNotes = summary.reviewNotes || {
                overallAssessment: '',
                issues: '',
                actionItems: '',
                resourceObservations: '',
                taskConcerns: '',
                featureTagNotes: '',
                additionalComments: '',
                goals: {
                    tasks: [],
                    features: [],
                    personal: [],
                    noGoals: false
                }
            };

            // Ensure goals structure exists
            if (!currentNotes.goals) {
                currentNotes.goals = {
                    tasks: [],
                    features: [],
                    personal: [],
                    noGoals: false
                };
            }

            // Find and update the personal goal
            const personalGoals = currentNotes.goals.personal || [];
            const goalIndex = personalGoals.findIndex(g => g.id === goalId);
            
            if (goalIndex === -1) {
                console.error('Personal goal not found:', goalId);
                alert('Goal not found. Please refresh the page.');
                return;
            }

            // Toggle status: pending <-> achieved
            const newStatus = currentStatus === 'pending' ? 'achieved' : 'pending';
            personalGoals[goalIndex] = {
                ...personalGoals[goalIndex],
                status: newStatus
            };

            // Update notes
            const updatedNotes = {
                ...currentNotes,
                goals: {
                    ...currentNotes.goals,
                    personal: personalGoals
                }
            };

            // Save updated notes
            await dailyReviewService.saveNotes(projectId, updatedNotes, date);

            // Update local state to reflect the change without refetching
            if (outcomeData && outcomeData.managerGoals) {
                const updatedManagerGoals = {
                    ...outcomeData.managerGoals,
                    personal: outcomeData.managerGoals.personal.map(goal => 
                        goal.id === goalId 
                            ? { ...goal, status: newStatus }
                            : goal
                    )
                };
                setOutcomeData({
                    ...outcomeData,
                    managerGoals: updatedManagerGoals
                });
            }
        } catch (err) {
            console.error('Error toggling personal goal:', err);
            alert('Failed to update goal status. Please try again.');
        } finally {
            setIsSavingGoal(false);
        }
    };

    if (loading) {
        return (
            <div className="today-outcome-page">
                <div className="today-outcome-loading">
                    <div className="loading-spinner" />
                    <p>Loading today's project outcome...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="today-outcome-page">
                <div className="today-outcome-error">
                    <p>{error}</p>
                    <Button onClick={fetchOutcomeData}>Retry</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="today-outcome-page">
            <div className="today-outcome-header">
                <div className="today-outcome-header-top">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherArrowLeft />}
                        onClick={handleBack}
                    >
                        Back
                    </Button>
                    <div className="today-outcome-title-section">
                        <h1 className="today-outcome-title">
                            Today's Project Outcome
                        </h1>
                        {outcomeData?.project && (
                            <div className="today-outcome-project-info">
                                <span className="today-outcome-project-name">{outcomeData.project.name}</span>
                                {outcomeData.project.abbreviation && (
                                    <span className="today-outcome-project-abbr">({outcomeData.project.abbreviation})</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="today-outcome-header-actions">
                    <DateSelector date={date} onDateChange={handleDateChange} />
                    <div className="today-outcome-actions">
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherFileText />}
                            onClick={handleViewNotes}
                        >
                            View Complete Notes
                        </Button>
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherDownload />}
                            onClick={handleExport}
                        >
                            Export
                        </Button>
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherPrinter />}
                            onClick={handlePrint}
                        >
                            Print
                        </Button>
                    </div>
                </div>
            </div>

            <div className="today-outcome-content">
                {outcomeData ? (
                    <TodayOutcomeComponent 
                        outcomeData={outcomeData} 
                        onPersonalGoalToggle={handlePersonalGoalToggle}
                        isSavingGoal={isSavingGoal}
                    />
                ) : (
                    <div className="today-outcome-empty">
                        <p>No outcome data available for this date.</p>
                    </div>
                )}
            </div>

            {notesData && (
                <NotesPreview
                    isOpen={showNotesModal}
                    onClose={() => setShowNotesModal(false)}
                    notes={notesData}
                />
            )}
        </div>
    );
};

export default TodayOutcome;

